import { useMutation } from '@apollo/client';
import { useRecoilValue } from 'recoil';
import { Markup } from 'interweave';

import ELEMENT from 'common/element';
import { Lab } from 'common/graphqlTypes';

import Button from 'components/common/Button/Button';
import Switch from 'components/common/Switch/Switch';
import Page from 'components/common/Page/Page';
import Link from 'components/common/Link/Link';
import PageHeader from 'components/common/PageHeader/PageHeader';

import './LabsPage.scss';
import { LAB_LIST, UPDATE_LAB } from 'components/labs/LabsPage/LabsPage.queries';
import { labsState } from 'components/account/account.state';

// labs we don't want to show the user
const HIDDEN_LABS = ['labsPage'];

function Header(): JSX.Element {
  return (
    <>
      <PageHeader title="Labs Experimental Features" />
      <div className="LabsPage__Header">
        <p>
          Scalyr Labs offers experimental, pre-release features that users can enable to try new functionality in
          Scalyr. Because Scalyr Labs are experimental, we do not offer any support for them and reserve the right to
          change or even remove them at any time.
        </p>
        <p>
          Please send us feedback at{' '}
          <Link href="mailto:labs-feedback@scalyr.com" className="link" testId={ELEMENT.labsPageHeaderEmail}>
            labs-feedback@scalyr.com
          </Link>
        </p>
      </div>
    </>
  );
}

type LabSectionProps = {
  lab: Lab;
  toggle: () => void;
};

function LabSection({ lab, toggle }: LabSectionProps): JSX.Element {
  const authorName = lab.author || 'No author';
  return (
    <div className="Lab" data-test-id={ELEMENT.labsPageLab}>
      <div className="Lab__Description">
        <h2 className="Lab__Title" data-test-id={ELEMENT.labsPageLabTitle}>
          {lab.title}
        </h2>
        <p className="Lab__Description__Authors">{authorName}</p>
        {lab.description ? (
          <div className="Lab__Description__Text">
            <Markup content={lab.description} />
          </div>
        ) : (
          <p>No description</p>
        )}
        <div className="Lab__Description__Actions" data-test-id={ELEMENT.labsPageLabDescriptionActions}>
          <Switch checked={lab.enabled} onChange={toggle} />
          <Button size="sm" href="mailto:labs-feedback@scalyr.com">
            Send Feedback
          </Button>
        </div>
      </div>
      {lab.imageSrc ? (
        <img src={lab.imageSrc} className="Lab__Preview" alt={`Scalyr Lab: ${lab.name}`} />
      ) : (
        <div className="Lab__Preview Lab__Preview--no-image">
          <span>No Image</span>
        </div>
      )}
    </div>
  );
}

function LabsPage(): JSX.Element {
  const labs = useRecoilValue(labsState);
  const [updateLab] = useMutation(UPDATE_LAB);

  function renderLabs(items: Lab[]): JSX.Element[] {
    return items.map((lab: Lab) => (
      <LabSection
        key={lab.id}
        lab={lab}
        toggle={(): void => {
          updateLab({
            variables: { name: lab.name, enable: !lab.enabled },
            refetchQueries: [{ query: LAB_LIST }],
          });
        }}
      />
    ));
  }

  function renderContent(): JSX.Element {
    // TO-DO Delete this filter when BE update provided labs list
    const filteredLabs: Lab[] = labs.filter(
      (lab: Lab) => lab.description !== null && !lab.required && !HIDDEN_LABS.includes(lab.name),
    );

    return (
      <>
        <Header />
        {filteredLabs.length > 0 ? renderLabs(filteredLabs) : <p>There are currently no labs available.</p>}
      </>
    );
  }

  return (
    <Page docTitle="Labs" testId={ELEMENT.labsPage}>
      {renderContent()}
    </Page>
  );
}

export default LabsPage;
